// Cuánto dura una sesión sin uso en un computador, y la marca que sobrevive a
// cerrar la pestaña.
//
// El cierre por inactividad (useIdleLogout) vive en un setInterval: solo corre
// mientras la pestaña está abierta. Eso deja afuera justo el caso del hospital
// que más importa — el médico apaga el computador o cierra el navegador sin
// cerrar sesión, y el que lo prende mañana entra a su ficha sin clave.
//
// Acá se guarda CUÁNDO fue la última actividad, en el navegador, y al cargar la
// app se compara contra el mismo umbral. Es un timestamp, no PHI.
//
// El umbral y la detección de aparato viven acá y no en el hook para que el
// chequeo de arranque no dependa de React ni arme un ciclo de imports. El hook
// los re-exporta.

const MINUTO = 60 * 1000;
export const IDLE_MS = 30 * MINUTO;
export const AVISO_MS = MINUTO; // aviso el último minuto

// Un computador táctil de hospital (all-in-one sin mouse conectado) no tiene
// ningún puntero fino, así que solo con `any-pointer` se clasificaba como
// celular: sesión abierta para siempre, justo en el equipo compartido que la
// protección existe para cubrir.
//
// El tamaño de la pantalla lo desempata, y se miran las DOS dimensiones y no
// solo el ancho. Con un umbral de ancho pelado había que elegir entre dejar
// afuera las estaciones de 1024x768 (comunes en pabellón) o empezar a agarrar
// tablets paradas. Pidiendo ancho Y alto, un teléfono común no entra por ninguna
// de las dos orientaciones — los más grandes rondan 956x440 acostados — y el 1024x768
// sí. Un plegable abierto sí entra (ver más abajo).
//
// Lo que queda del lado "computador" y es discutible: TODOS los iPad, no solo
// el grande — medido en producción, el mini (744x1133) y el Air (820x1180)
// también entran por el lado largo. Es el costo asumido, en línea con el iPad
// con teclado. Los datos: 7 logins desde iPad en 5 meses contra 274 desde
// Windows.
//
// TRAMPA ABIERTA (#1211), NO reproducida: Chrome en Android con "Sitio de
// escritorio" oculta el Android del user agent, así que la excepción del teléfono
// no lo ve. Se cierra cada 30 min por cualquiera de dos caminos: un Samsung que
// declara su táctil como touchpad (puntero fino), o `window.screen` en píxeles
// físicos (el quirk ReportScreenSizeInPhysicalPixels de Blink). Necesita un
// Android real para confirmarlo.
// Lo que SÍ está descartado en Chrome, leyendo la fuente de Blink: el zoom de
// página no achica `screen`, así que el caso "zoom al 200%" está cubierto (en
// Firefox no se midió). La escala del
// sistema sí la achica: un 1920x1080 al 200% reporta 960x540.
//
// TRAMPA ABIERTA (#1211): por esa misma escala, un all-in-one táctil SIN mouse al
// 200% no pasa por pantalla de PC y no cierra. Viene de antes de este arreglo.
//
// La comparación es SIMÉTRICA: se toma el lado largo y el corto de la pantalla,
// no ancho y alto. Con ancho/alto fijos el mismo aparato cambiaba de política al
// rotarlo — un all-in-one montado en vertical (768x1024) no cerraba nunca y
// acostado sí, que es un camino de evasión trivial. Con lado largo >= 900 y lado
// corto >= 600 no entra ninguna orientación de un teléfono común (~956x440 los más
// grandes, sin medir en cada modelo).
//
// TRAMPA ABIERTA (#1211), NO medida: un plegable abierto (882x1104) entra como
// pantalla de PC. Lo salva la excepción de `esEscritorio` solo si su navegador
// sigue diciendo "Mobile", y Chrome probablemente lo quita con el plegable
// abierto. Lo mismo un iPhone en modo escritorio con mouse: su user agent dice
// Macintosh y cierra.
const LADO_LARGO_PX = 900;
const LADO_CORTO_PX = 600;

/** El navegador se declara teléfono. Una tablet Android no lleva "Mobile". */
export const uaDeTelefono = () =>
  navigator.userAgentData?.mobile === true ||
  /iPhone|Android.*Mobile/.test(navigator.userAgent || "");

/**
 * true si hay un puntero fino o la pantalla es de PC, salvo en un teléfono
 * (pantalla táctil Y navegador que se declara teléfono). No cubre todo
 * computador: el all-in-one táctil sin mouse a escala alta da false (TRAMPA
 * ABIERTA arriba).
 */
export const esEscritorio = () => {
  try {
    // Se documenta que los Samsung declaran su táctil como touchpad, también en
    // Chrome: puntero fino y hover en un teléfono, que se cerraba cada 30 min
    // (#1211, 30-oct-2026; ctrl.blog/entry/css-media-hover-samsung, sin confirmar
    // en un Samsung real al escribir esto).
    //
    // Por qué no basta con el táctil: un notebook táctil 1920x1080 con escala al
    // 200% reporta `screen` 960x540, que no pasa por pantalla de PC, y su puntero
    // fino es lo único que lo hace cerrar. Por qué se piden las dos señales: el
    // user agent solo se puede cambiar a mano, y así no basta para esquivar el
    // cierre en un equipo sin pantalla táctil.
    //
    // Costo aceptado (Sebastián, 31-oct-2026: «Aceptarlo» y «Sí, lo cubre»):
    // cualquier PC donde alguien fuerza a propósito el modo celular del navegador
    // queda sin el cierre de 30 min. Con pantalla táctil basta cambiar el user
    // agent; sin ella, DevTools en modo dispositivo simula el toque y el user
    // agent de teléfono a la vez (lo midió el revisor v6). No abre una vía nueva:
    // con DevTools ya se podía mantener viva la sesión desde la consola.
    if (Number(navigator.maxTouchPoints) > 0 && uaDeTelefono()) return false;

    // Fuera de esa excepción, nada de sniffear el user-agent: miente. Se pregunta
    // por el hardware.
    //
    // `any-pointer`/`any-hover` y no `pointer`/`hover`: un notebook con pantalla
    // táctil reporta el táctil como puntero PRIMARIO y se colaba como celular.
    // Con `any-*` alcanza con que exista un mouse o trackpad.
    //
    // Tampoco se descarta la PWA instalada: instalar la app en el computador del
    // hospital no puede ser la forma de esquivar el cierre de sesión.
    const punteroFino = window.matchMedia("(any-pointer: fine) and (any-hover: hover)").matches;

    // Se mide la PANTALLA (`screen`), no la ventana. Con media queries, que
    // miden el viewport, un all-in-one táctil con la ventana a media pantalla
    // (683x730) o con zoom al 200% se declaraba celular y no cerraba nunca:
    // achicar la ventana apagaba el control. El aparato no cambia de tamaño
    // porque el usuario mueva un borde.
    const ancho = Number(window.screen?.width) || 0;
    const alto = Number(window.screen?.height) || 0;
    const largo = Math.max(ancho, alto);
    const corto = Math.min(ancho, alto);
    const pantallaDePC = largo >= LADO_LARGO_PX && corto >= LADO_CORTO_PX;

    return punteroFino || pantallaDePC;
  } catch {
    // Falla CERRADO: si no se puede preguntar, se asume computador. Al revés,
    // cualquier excepción de matchMedia apagaba el cierre de sesión en silencio
    // y sin señal, que es la peor forma de perder un control de seguridad. El
    // costo del lado seguro es pedirle la clave de nuevo a alguien; el del otro
    // lado es una ficha abierta en un puesto compartido.
    return true;
  }
};

const RUTAS_PUBLICAS_AUTH = ["/authentication", "/recuperar-clave", "/nueva-clave"];

export const enRutaPublicaDeAuth = () => {
  try {
    // Sin normalizar el slash final, "/nueva-clave/" no matcheaba acá pero React
    // Router sí lo monta: el enlace del correo con un slash de más volvía a
    // terminar en el login, con el token borrado de la URL.
    const ruta = window.location.pathname.replace(/\/+$/, "").toLowerCase() || "/";
    return RUTAS_PUBLICAS_AUTH.includes(ruta);
  } catch {
    return false;
  }
};

const CLAVE_MARCA = "smartdx.idle.ultima";
const CLAVE_EXENTO = "smartdx.idle.exento";

// Escribir en localStorage en cada mousemove sería caro y no compra nada: la
// ventana que se mide es de 30 min, un desfase de 30 s es ruido.
const THROTTLE_MS = 30 * 1000;
let ultimaEscritura = 0;

/** Deja constancia de que el usuario está acá. Throttleada a 30 s. */
export const marcarActividad = ({ forzar = false } = {}) => {
  const ahora = Date.now();
  if (!forzar && ahora - ultimaEscritura < THROTTLE_MS) return;
  ultimaEscritura = ahora;
  try {
    localStorage.setItem(CLAVE_MARCA, String(ahora));
  } catch {
    /* modo privado o storage lleno: la protección degrada, nunca rompe la app */
  }
};

// El chequeo de arranque corre ANTES de que vuelva /api/user/me/, así que sin
// este cache no sabríamos que el que está entrando es alguien exento y lo
// mandaríamos al login por error. Se refresca cada vez que llega el usuario.
export const recordarExencion = (exento) => {
  try {
    // Se escribe "0", no se borra: "no es exento" y "todavía no sé" son cosas
    // distintas, y confundirlas mandaba a Hans al login. Ver `exencionConocida`.
    localStorage.setItem(CLAVE_EXENTO, exento ? "1" : "0");
  } catch {
    /* ídem */
  }
};

export const estaExento = () => {
  try {
    return localStorage.getItem(CLAVE_EXENTO) === "1";
  } catch {
    return false;
  }
};

/**
 * ¿Sabemos si este usuario es exento, o todavía no volvió `/api/user/me/`?
 *
 * Sin esto el arranque trataba "no sé" como "no es exento" y cerraba igual. A
 * Hans, que trabaja siempre en los mismos equipos y por eso está exento, le
 * bastaba abrir en un navegador nuevo — o que fallara una escritura del cache,
 * plausible con el diario del dictado ocupando el storage — para terminar en el
 * login. Es exactamente lo que la excepción existe para evitar.
 */
export const exencionConocida = () => {
  try {
    return localStorage.getItem(CLAVE_EXENTO) !== null;
  } catch {
    return false;
  }
};

/**
 * Última actividad conocida en ESTE navegador, mirando todas las pestañas.
 *
 * La marca vive en `localStorage`, que es del origen entero, así que escribir en
 * una pestaña la actualiza para todas. Es lo que impide que una pestaña de fondo
 * cierre la sesión mientras el médico trabaja en otra: sin esto cada pestaña
 * medía solo su propia inactividad, la de atrás vencía sola, y `logout()` es
 * global — se llevaba el token Y el diario del dictado de la pestaña activa,
 * con texto clínico sin guardar adentro.
 *
 * Devuelve 0 si no hay marca; el llamador combina con su propio reloj.
 */
export const ultimaActividadConocida = () => {
  try {
    const marca = Number(localStorage.getItem(CLAVE_MARCA));
    if (!marca || Number.isNaN(marca)) return 0;
    // Acotada al presente: una marca en el FUTURO hacía la sesión inmortal, y
    // no hace falta mala fe para producirla — un PC de hospital con la hora
    // adelantada que después se sincroniza deja una. Antes solo engañaba al
    // chequeo de arranque; desde que el temporizador también lee esta marca,
    // apagaría el reloj de la pestaña que está corriendo.
    return Math.min(marca, Date.now());
  } catch {
    return 0;
  }
};

/** Al cerrar sesión y al entrar: el que viene no hereda la marca del anterior. */
export const limpiarMarca = () => {
  ultimaEscritura = 0;
  try {
    localStorage.removeItem(CLAVE_MARCA);
    localStorage.removeItem(CLAVE_EXENTO);
  } catch {
    /* ídem */
  }
};

/**
 * true si hay una sesión de computador que ya pasó el umbral de inactividad.
 *
 * NO mira la ruta ni decide qué hacer: eso lo resuelve quien llama, porque las
 * dos respuestas posibles son distintas (ver App.js).
 *
 * Sin marca devuelve false a propósito: es el primer arranque después de
 * actualizar la app, y sacar a alguien que acaba de entrar sería peor que
 * esperar a la próxima vez.
 *
 * @param {object} opts
 * @param {boolean} opts.ignorarExencion — para el llamador que YA sabe, por el
 *   backend, si el usuario es exento. El cache local puede estar rancio (quedó
 *   el "1" del médico anterior en ese computador) y creerle es justamente el
 *   agujero: se salteaba el vencimiento y la sesión ajena revivía.
 *
 * Dos límites conocidos de confiar en el cache al arrancar, que es el precio de
 * no sacar a Hans antes de saber que es Hans:
 *
 *  - Con una exención rancia, la app alcanza a pedir la lista de pacientes
 *    (~230 ms) antes de que el guard cierre al volver /api/user/me/. Se ve la
 *    pizarra un instante. Cerrarlo del todo pide retener el render hasta tener
 *    el usuario, que es otro cambio.
 *  - Si /api/user/me/ nunca vuelve, no hay `currentUser`, así que no corre ni la
 *    revalidación ni el temporizador y esa sesión no muere. Es previo a este
 *    cambio, pero conviene saberlo antes de apoyarse en esto.
 */
export const sesionVencida = ({ ignorarExencion = false } = {}) => {
  try {
    if (!localStorage.getItem("authToken")) return false;
    if (!esEscritorio()) return false;
    if (!ignorarExencion) {
      // Mientras no se sepa, no se decide: cerrarle la sesión a un exento es
      // peor que esperar los milisegundos que tarda el backend en decir quién
      // es. El guard revalida en cuanto llega `/api/user/me/`.
      if (!exencionConocida() || estaExento()) return false;
    }
    const marca = Number(localStorage.getItem(CLAVE_MARCA));
    if (!marca || Number.isNaN(marca)) return false;
    return Date.now() - marca > IDLE_MS;
  } catch {
    return false;
  }
};
